import {getTranslation} from "./helpers";
import utils from "../utils/utils";
import {getSiteVar} from "../services/site-vars";
import {clearCloudAuthFromSession, readCloudAuthFromSession, writeCloudAuthToSession} from "./cloud-auth-storage";
import {userMessage} from "./user-message";

const cloudAuthRefreshSkewMs = 60 * 1000;
const cloudRequestTimeoutMs = 120 * 1000;

function createPopupCloudError(message, code = 'popup_cloud_error', extra = {}) {
    const error = new Error(String(message || getTranslation('popup_load_error')));
    error.code = code;
    Object.assign(error, extra);
    return error;
}

function ajaxPromise(ajaxSettings = {}) {
    return new Promise((resolve, reject) => {
        $.ajax({
            ...ajaxSettings,
            success: (response, textStatus, jqXHR) => resolve({response, textStatus, jqXHR}),
            error: (xhr, status, error) => reject({xhr, status, error}),
        });
    });
}

function isWpNonceFailureResponse(jqXHR) {
    if (!jqXHR || Number.parseInt(jqXHR.status, 10) !== 403) {
        return false;
    }

    return String(jqXHR.responseText || '').trim() === '-1';
}

async function ajaxWithFreshNonceRetry(ajaxSettingsBuilder) {
    const request = () => ajaxPromise(ajaxSettingsBuilder());

    try {
        return await request();
    } catch (err) {
        if (!isWpNonceFailureResponse(err?.xhr)) {
            throw err;
        }

        await utils.fetch_fresh_nonce(true);
        return request();
    }
}

function normalizeStoredAuth(authPayload = null) {
    if (!authPayload || typeof authPayload !== 'object') {
        return null;
    }

    const functionUrl = String(authPayload.functionUrl || '').trim();
    const authToken = String(authPayload.authToken || '').trim();
    const expiresAtMs = Number.parseInt(authPayload.expiresAtMs, 10) || 0;
    const requestPayload = authPayload.requestPayload && typeof authPayload.requestPayload === 'object'
        ? {...authPayload.requestPayload}
        : {};

    if (!functionUrl || !authToken || expiresAtMs <= 0) {
        return null;
    }

    return {
        functionUrl,
        authToken,
        expiresAtMs,
        requestPayload,
    };
}

function normalizeAuthResponse(response = null, label = 'Popup') {
    if (!response || response.success !== true || !response.data || typeof response.data !== 'object') {
        return null;
    }

    const functionUrl = String(response.data.function_url || '').trim();
    const authToken = String(response.data.auth_token || '').trim();
    const expiresAt = Number.parseInt(response.data.expires_at, 10) || 0;
    const requestPayload = response.data.request_payload && typeof response.data.request_payload === 'object'
        ? response.data.request_payload
        : {};

    if (!functionUrl || !authToken || expiresAt <= 0) {
        throw createPopupCloudError(
            `${label} cloud auth response was incomplete.`,
            'popup_cloud_bootstrap_failed',
        );
    }

    return normalizeStoredAuth({
        functionUrl,
        authToken,
        expiresAtMs: expiresAt * 1000,
        requestPayload,
    });
}

function normalizeCloudResponse(response = null, label = 'Popup') {
    if (
        response &&
        typeof response === 'object' &&
        !Array.isArray(response) &&
        response.success === true &&
        typeof response.html === 'string' &&
        response.html.trim() !== ''
    ) {
        return response.html;
    }

    const message = response && typeof response === 'object'
        ? String(response.message || response.data?.message || '')
        : '';

    throw createPopupCloudError(
        message || `${label} cloud request failed.`,
        'popup_cloud_invalid_response',
    );
}

function normalizeRequestError(err, label = 'Popup') {
    if (err instanceof Error && err.code) {
        return err;
    }

    if (err?.xhr?.status === 401 || err?.xhr?.status === 403) {
        return createPopupCloudError(
            getTranslation('popup_access_denied'),
            'popup_cloud_unauthorized',
            {cause: err},
        );
    }

    const responseMessage = getCloudResponseMessage(err);
    if (responseMessage) {
        return createPopupCloudError(
            responseMessage,
            'popup_cloud_request_failed',
            {cause: err, status: err?.xhr?.status},
        );
    }

    if (err?.error) {
        return createPopupCloudError(
            String(err.error),
            'popup_cloud_request_failed',
            {cause: err},
        );
    }

    return createPopupCloudError(
        err?.message || getTranslation('popup_load_error'),
        'popup_cloud_request_failed',
        {cause: err},
    );
}

function buildBaseRequestData(data = {}) {
    const requestData = data && typeof data === 'object' ? {...data} : {};
    requestData._wpnonce = utils.get_nonce();

    if (!requestData.post_id) {
        requestData.post_id = utils.get_post_id();
    }

    return requestData;
}

function getContextKey(storageKey, baseData = {}) {
    return [
        storageKey,
        'v1',
        Number.parseInt(getSiteVar('current_user_id'), 10) || 0,
        Number.parseInt(baseData.post_id, 10) || 0,
    ].join(':');
}

function hasFreshAuth(authPayload = null) {
    const expiresAtMs = Number.parseInt(authPayload?.expiresAtMs, 10) || 0;
    if (!authPayload || !authPayload.authToken || !authPayload.functionUrl || expiresAtMs <= 0) {
        return false;
    }

    return expiresAtMs > (Date.now() + cloudAuthRefreshSkewMs);
}

function isCloudUnauthorized(err) {
    return !!(err?.xhr && (err.xhr.status === 401 || err.xhr.status === 403));
}

function getCloudResponseMessage(err) {
    return String(
        userMessage(err?.xhr?.responseJSON) ||
        userMessage(err?.xhr?.responseJSON?.data) ||
        err?.xhr?.responseText ||
        ''
    ).trim();
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export function renderPopupCloudErrorHtml(message = '') {
    const detail = String(message || '').trim();
    const detailHtml = detail ? `<p class="popup-cloud-error__detail">${escapeHtml(detail)}</p>` : '';
    return `<div class="grid-container"><div class="grid-x"><div class="small-12 cell"><p>${escapeHtml(getTranslation('generic_retry'))}</p>${detailHtml}</div></div></div>`;
}

export function createPopupCloudHtmlRequester({
    authAction,
    authMode = 'cached',
    storageKey,
    label,
    buildCloudPayload,
}) {
    const authMap = new Map();
    const authPromiseMap = new Map();

    function clearAuth(baseData) {
        const contextKey = getContextKey(storageKey, baseData);
        authMap.delete(contextKey);
        authPromiseMap.delete(contextKey);
        clearCloudAuthFromSession({
            bootstrapAction: authAction,
            contextKey,
        });
    }

    async function fetchAuth(baseData, forceRefresh = false) {
        const contextKey = getContextKey(storageKey, baseData);
        if (forceRefresh) {
            clearAuth(baseData);
        }

        if (!authMap.has(contextKey)) {
            const storedAuth = readCloudAuthFromSession({
                bootstrapAction: authAction,
                contextKey,
                normalizeAuthPayload: normalizeStoredAuth,
            });

            if (storedAuth) {
                authMap.set(contextKey, storedAuth);
            }
        }

        const currentAuth = authMap.get(contextKey) || null;
        if (hasFreshAuth(currentAuth)) {
            return currentAuth;
        }

        if (authPromiseMap.has(contextKey)) {
            return authPromiseMap.get(contextKey);
        }

        const authPromise = ajaxWithFreshNonceRetry(() => ({
            type: 'POST',
            url: utils.get_ajax_url(),
            dataType: 'json',
            wl_skip_nonce_retry: true,
            data: {
                action: authAction,
                post_id: baseData.post_id,
                _wpnonce: utils.get_nonce(),
            },
        })).then(({response}) => {
            const cloudAuth = normalizeAuthResponse(response, label);
            if (!cloudAuth) {
                throw createPopupCloudError(
                    `Failed to initialize ${label.toLowerCase()} cloud access.`,
                    'popup_cloud_bootstrap_failed',
                );
            }

            authMap.set(contextKey, cloudAuth);
            writeCloudAuthToSession({
                bootstrapAction: authAction,
                contextKey,
                authPayload: cloudAuth,
                normalizeAuthPayload: normalizeStoredAuth,
            });

            return cloudAuth;
        }).finally(() => {
            authPromiseMap.delete(contextKey);
        });

        authPromiseMap.set(contextKey, authPromise);
        return authPromise;
    }

    async function requestCloudHtml(cloudAuth, baseData) {
        const {response} = await ajaxPromise({
            type: 'POST',
            url: cloudAuth.functionUrl,
            dataType: 'json',
            crossDomain: true,
            timeout: cloudRequestTimeoutMs,
            contentType: 'application/json',
            processData: false,
            data: JSON.stringify({
                ...cloudAuth.requestPayload,
                ...buildCloudPayload(baseData),
                auth_token: cloudAuth.authToken,
            }),
        });

        return normalizeCloudResponse(response, label);
    }

    return async function requestPopupCloudHtml(data = {}) {
        const baseData = buildBaseRequestData(data);

        try {
            let cloudAuth = await fetchAuth(baseData, authMode === 'fresh');

            try {
                return await requestCloudHtml(cloudAuth, baseData);
            } catch (err) {
                if (!isCloudUnauthorized(err)) {
                    throw err;
                }

                cloudAuth = await fetchAuth(baseData, true);
                return requestCloudHtml(cloudAuth, baseData);
            }
        } catch (err) {
            throw normalizeRequestError(err, label);
        }
    };
}
